import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, FileDown, PanelLeftClose, PanelLeftOpen, Send, SlidersHorizontal } from 'lucide-react';
import { formatDate } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  Icon,
  MenuItem,
  MenuList,
  Popover,
  Segmented,
  Skeleton,
  Tooltip,
  useToast,
} from '../../../ds/components';
import { useIsCompact, usePrefersReducedMotion } from '../../../ds/hooks';
import { ApiError, errorMessage } from '../../../lib/api';
import {
  createInvestmentCalculation,
  downloadCalculationPdf,
  propertyKeys,
  useCalculatorContext,
} from '../api';
import type { Calculation, CalculatorContext, InvestmentInputValues } from '../types';
import { AdvisorTab } from './AdvisorTab';
import { Accordion, INPUT_GROUPS, InputsColumn, inputGroupSelector, type InputGroupKey } from './InputsTab';
import { readInputsCollapsed, storeInputsCollapsed } from './inputsPanel';
import {
  buildAdvisorView,
  buildTextSummary,
  buildView,
  costSplitFromContext,
  deductibleSum,
  eur,
  isCostSplitKey,
  sgn,
  type CostSplit,
  type Horizon,
  type InputKey,
  type ParamKey,
} from './model';
import { ResultsTab } from './ResultsTab';
import { SaveDialog, defaultLabel } from './SaveDialog';
import styles from './calculator.module.css';

type View = 'berater' | 'kunde';

const VIEWS: { value: View; label: string }[] = [
  { value: 'berater', label: 'Berateransicht' },
  { value: 'kunde', label: 'Kundenansicht' },
];

/** Header action „PDF" with its menu „PDF speichern" / „An Mandanten senden". */
function PdfMenu({
  pending,
  onDownload,
  onSend,
}: {
  pending: boolean;
  onDownload: () => void;
  onSend: () => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        ref={anchorRef}
        className={styles.headerPrimary}
        loading={pending}
        aria-haspopup="menu"
        aria-expanded={open}
        iconEnd={<Icon icon={ChevronDown} size={16} />}
        onClick={() => setOpen((current) => !current)}
      >
        PDF
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        width={238}
        placement="bottom-end"
        variant="card"
        initialFocus="none"
      >
        <MenuList label="PDF" density="compact" onClose={() => setOpen(false)}>
          <MenuItem
            label="PDF speichern"
            icon={<Icon icon={FileDown} size={16} />}
            onSelect={() => {
              setOpen(false);
              onDownload();
            }}
          />
          <MenuItem
            label="An Mandanten senden"
            icon={<Icon icon={Send} size={16} />}
            onSelect={() => {
              setOpen(false);
              onSend();
            }}
          />
        </MenuList>
      </Popover>
    </>
  );
}

function Header({
  context,
  propertyId,
  saved,
  pdfPending,
  onReset,
  onSave,
  onDownloadPdf,
  onSendPdf,
  view,
  onView,
}: {
  context: CalculatorContext;
  propertyId: string;
  saved: Calculation | null;
  pdfPending: boolean;
  onReset: () => void;
  onSave: () => void;
  onDownloadPdf: () => void;
  onSendPdf: () => void;
  view: View;
  onView: (view: View) => void;
}) {
  const { unit, contact, deal } = context;
  const meta = context.lastChangedAt
    ? `Betreuer ${context.advisorShort ?? '—'} · zuletzt geändert ${formatDate(context.lastChangedAt)}`
    : 'Noch nicht gespeichert';
  const eyebrow = [
    saved ? saved.numberLabel : 'Berechnung',
    contact?.name ?? deal?.label ?? 'Kein Mandant gewählt',
  ].join(' · ');
  return (
    <>
      <nav className={styles.crumbs} aria-label="Pfad">
        <Link to="/properties" className={styles.crumbLink}>
          Objekte
        </Link>
        <span aria-hidden="true">›</span>
        <Link to={`/properties/${propertyId}`} className={styles.crumbLink}>
          {unit.propertyName}
        </Link>
        <span aria-hidden="true">›</span>
        <Link to={`/properties/${propertyId}/units/${unit.id}`} className={styles.crumbLink}>
          {unit.number}
        </Link>
        <span aria-hidden="true">›</span>
        <span className={styles.crumbCurrent} aria-current="page">
          Immobilienrechner
        </span>
        <span className={styles.crumbMeta}>{meta}</span>
      </nav>
      <div className={styles.titleRow}>
        <div>
          <div className={styles.eyebrow10}>{eyebrow}</div>
          <h1 className={styles.title}>Immobilienrechner · {unit.number}</h1>
        </div>
        <span className={styles.draftPill}>{saved ? `${saved.numberLabel} gespeichert` : 'Entwurf'}</span>
        <div className={styles.actions}>
          <button type="button" className={clsx('pressable', styles.headerButton)} onClick={onReset}>
            Annahmen zurücksetzen
          </button>
          <button type="button" className={clsx('pressable', styles.headerButton)} onClick={onSave}>
            Im Deal speichern
          </button>
          <PdfMenu pending={pdfPending} onDownload={onDownloadPdf} onSend={onSendPdf} />
        </div>
      </div>
      <div className={styles.viewBar}>
        <Segmented<View> label="Ansicht" items={VIEWS} value={view} onChange={onView} />
      </div>
    </>
  );
}

/** The collapsed inputs column (72 px): expand button and one shortcut per parameter group. */
function InputsRail({
  expandRef,
  onExpand,
  onGroup,
}: {
  expandRef: RefObject<HTMLButtonElement | null>;
  onExpand: () => void;
  onGroup: (group: InputGroupKey) => void;
}) {
  return (
    <div className={styles.inputsRail}>
      <Tooltip content="Eingaben ausklappen" placement="bottom-start">
        <button
          ref={expandRef}
          type="button"
          className={clsx('pressable', styles.railButton)}
          aria-label="Eingaben ausklappen"
          aria-expanded={false}
          onClick={onExpand}
        >
          <Icon icon={PanelLeftOpen} size={20} />
        </button>
      </Tooltip>
      <span className={styles.railDivider} aria-hidden="true" />
      {INPUT_GROUPS.map((group) => (
        <Tooltip key={group.key} content={group.title} placement="bottom-start">
          <button
            type="button"
            className={clsx('pressable', styles.railButton)}
            aria-label={`Eingaben ausklappen: ${group.title}`}
            onClick={() => onGroup(group.key)}
          >
            <Icon icon={group.icon} size={20} />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}

/** `/properties/:id/calculator?unitId=&contactId=&dealId=&view=berater|kunde` — Immobilienrechner. */
export function CalculatorPage() {
  const { id: propertyId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const compact = useIsCompact();
  const reducedMotion = usePrefersReducedMotion();
  const unitId = params.get('unitId') ?? '';
  const contactId = params.get('contactId') ?? '';
  const dealId = params.get('dealId') ?? '';
  const view: View = params.get('view') === 'kunde' ? 'kunde' : 'berater';
  const contextQuery = useCalculatorContext({
    ...(unitId ? { unitId } : {}),
    ...(contactId ? { contactId } : {}),
    ...(dealId ? { dealId } : {}),
  });
  const context = contextQuery.data ?? null;

  const [values, setValues] = useState<InvestmentInputValues | null>(null);
  const [split, setSplit] = useState<CostSplit | null>(null);
  const [touched, setTouched] = useState<Set<ParamKey>>(() => new Set());
  // The advisor view has no horizon switch any more (D-386); the summary and the inputs line use 10 years.
  const horizon: Horizon = 10;
  const [inputsOpen, setInputsOpen] = useState(false);
  const [dialog, setDialog] = useState<'save' | 'pdf' | null>(null);
  const [saved, setSaved] = useState<Calculation | null>(null);
  const [pdfPending, setPdfPending] = useState(false);
  const [inputsCollapsed, setInputsCollapsed] = useState(readInputsCollapsed);
  const [focusGroup, setFocusGroup] = useState<InputGroupKey | null>(null);
  const inputsRef = useRef<HTMLDivElement>(null);
  const collapseRef = useRef<HTMLButtonElement>(null);
  const expandRef = useRef<HTMLButtonElement>(null);
  /** Set by the toggle buttons so focus follows to the other state's toggle (not on the first render). */
  const toggledRef = useRef(false);

  const setCollapsed = useCallback((collapsed: boolean) => {
    toggledRef.current = true;
    setInputsCollapsed(collapsed);
    storeInputsCollapsed(collapsed);
  }, []);

  useEffect(() => {
    if (!toggledRef.current) return;
    toggledRef.current = false;
    if (inputsCollapsed) expandRef.current?.focus();
    else if (focusGroup === null) collapseRef.current?.focus();
  }, [inputsCollapsed]);

  // A rail shortcut expands the column and then brings its group into view (after the column is rendered again).
  useEffect(() => {
    if (focusGroup === null || inputsCollapsed) return;
    const target = inputsRef.current?.querySelector<HTMLElement>(inputGroupSelector(focusGroup));
    setFocusGroup(null);
    if (!target) return;
    target.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' });
    target.querySelector<HTMLElement>('input, button, select, textarea')?.focus({ preventScroll: true });
  }, [focusGroup, inputsCollapsed, reducedMotion]);

  // Seed from the context defaults; a new context (contact chosen) refreshes the untouched fields.
  useEffect(() => {
    if (!context) return;
    const nextSplit = costSplitFromContext(context);
    if (split) {
      for (const key of Object.keys(nextSplit) as (keyof CostSplit)[]) {
        if (touched.has(key)) nextSplit[key] = split[key];
      }
    }
    const nextValues = { ...context.defaults };
    if (values) {
      for (const key of Object.keys(context.defaults) as InputKey[]) {
        if (touched.has(key)) Object.assign(nextValues, { [key]: values[key] });
      }
    }
    nextValues.deductibleCostsCents = deductibleSum(nextSplit);
    setSplit(nextSplit);
    setValues(nextValues);
    // `touched`, `values` and `split` are read, not triggers: the seed runs when the context changes.
  }, [context]);

  const customer = useMemo(
    () => (values && split ? buildView(values, split, context) : null),
    [values, split, context],
  );
  const advisor = useMemo(
    () => (values && customer ? buildAdvisorView(values, customer.result, horizon) : null),
    [values, customer, horizon],
  );

  const markTouched = useCallback((key: ParamKey) => {
    setTouched((current) => {
      if (current.has(key)) return current;
      const next = new Set(current);
      next.add(key);
      return next;
    });
  }, []);

  const setValue = useCallback(
    (key: ParamKey, value: number) => {
      if (isCostSplitKey(key)) {
        if (!split) return;
        const next = { ...split, [key]: value };
        setSplit(next);
        setValues((current) =>
          current ? { ...current, deductibleCostsCents: deductibleSum(next) } : current,
        );
      } else {
        setValues((current) => (current ? { ...current, [key]: value } : current));
      }
      markTouched(key);
    },
    [markTouched, split],
  );

  const setSellerPays = useCallback(
    (sellerPays: boolean) => {
      setValues((current) => (current ? { ...current, sellerPaysAcquisitionCosts: sellerPays } : current));
      markTouched('sellerPaysAcquisitionCosts');
    },
    [markTouched],
  );

  const patchValues = useCallback(
    (patch: Partial<InvestmentInputValues>) => {
      setValues((current) => (current ? { ...current, ...patch } : current));
      for (const key of Object.keys(patch) as InputKey[]) markTouched(key);
    },
    [markTouched],
  );

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      setParams(next);
    },
    [params, setParams],
  );

  if (!unitId && !dealId) {
    return (
      <div className={clsx(styles.page, styles.state)}>
        <Banner
          tone="neutral"
          title="Für den Immobilienrechner wird eine Einheit benötigt."
          actions={
            <Link to={`/properties/${propertyId}`} className={clsx('pressable', styles.headerButton)}>
              Zur Objektakte
            </Link>
          }
        >
          Öffnen Sie den Rechner über „Rechner starten“ in der Einheitenakte oder die Einheitenliste der
          Objektakte.
        </Banner>
      </div>
    );
  }

  if (contextQuery.isPending || !values || !split || !customer || !advisor || !context) {
    if (contextQuery.isError) {
      const notFound = contextQuery.error instanceof ApiError && contextQuery.error.status === 404;
      return (
        <div className={clsx(styles.page, styles.state)}>
          <Banner
            tone={notFound ? 'neutral' : 'danger'}
            title={
              notFound ? 'Die Einheit wurde nicht gefunden.' : 'Der Rechner konnte nicht geladen werden.'
            }
            actions={
              <Button variant="secondary" size="small" onClick={() => void contextQuery.refetch()}>
                Erneut versuchen
              </Button>
            }
          >
            {errorMessage(contextQuery.error)}
          </Banner>
        </div>
      );
    }
    return (
      <div className={clsx(styles.page, styles.state)} aria-busy="true">
        <Skeleton width={320} height={12} />
        <div style={{ marginTop: 14 }}>
          <Skeleton width={260} height={26} />
        </div>
        <div style={{ marginTop: 24 }}>
          <Skeleton variant="block" height={420} />
        </div>
      </div>
    );
  }

  const inputs = (
    <InputsColumn
      context={context}
      values={values}
      split={split}
      onChange={setValue}
      onSellerPaysChange={setSellerPays}
      onPatch={patchValues}
      onContactChange={(nextContactId) => setParam('contactId', nextContactId)}
    />
  );
  const rememberSaved = (calculation: Calculation) => {
    setSaved(calculation);
    void queryClient.invalidateQueries({ queryKey: propertyKeys.unitCalculations(context.unit.id) });
  };

  // „PDF speichern": a new version exactly like „Im Deal speichern" (deal, default label, no note, no mail),
  // then the generated PDF is downloaded with the session.
  const savePdf = async () => {
    if (!context.contact) {
      toast({ message: 'Bitte zuerst unter „Angaben“ einen Mandanten wählen.' });
      return;
    }
    setPdfPending(true);
    let calculation: Calculation;
    try {
      calculation = await createInvestmentCalculation({
        contactId: context.contact.id,
        unitId: context.unit.id,
        ...(context.deal ? { dealId: context.deal.id } : {}),
        label: defaultLabel(context, values),
        note: null,
        inputs: values,
        sendPdf: false,
      });
    } catch (err) {
      setPdfPending(false);
      toast({ message: errorMessage(err) });
      return;
    }
    rememberSaved(calculation);
    try {
      await downloadCalculationPdf(calculation);
      toast({
        message: `${calculation.numberLabel} · V${calculation.version} gespeichert, PDF heruntergeladen.`,
      });
    } catch (err) {
      toast({
        message: `${calculation.numberLabel} · V${calculation.version} gespeichert. ${errorMessage(err)}`,
      });
    } finally {
      setPdfPending(false);
    }
  };

  const own = advisor.balance.ownShareCents;
  const inputsSummary = `Kaufpreis ${eur(values.purchasePriceCents)} · ${own > 0 ? 'Eigenanteil' : 'Überschuss'} ${sgn(Math.abs(own))} € / Monat`;

  return (
    <div className={styles.page}>
      <Header
        context={context}
        propertyId={propertyId || context.unit.propertyId}
        saved={saved}
        view={view}
        onView={(next) => setParam('view', next === 'berater' ? null : next)}
        onReset={() => {
          const seeded = costSplitFromContext(context);
          setSplit(seeded);
          setValues({ ...context.defaults, deductibleCostsCents: deductibleSum(seeded) });
          setTouched(new Set());
          toast({ message: 'Annahmen zurückgesetzt.' });
        }}
        pdfPending={pdfPending}
        onSave={() => setDialog('save')}
        onDownloadPdf={() => void savePdf()}
        onSendPdf={() => setDialog('pdf')}
      />
      <div className={styles.workspace}>
        {compact ? (
          <div className={styles.inputsCompact}>
            <Accordion
              icon={<Icon icon={SlidersHorizontal} size={20} />}
              title="Angaben"
              subtitle={inputsSummary}
              open={inputsOpen}
              onToggle={() => setInputsOpen((current) => !current)}
            >
              {inputs}
            </Accordion>
          </div>
        ) : (
          <aside
            className={clsx(styles.inputsAside, inputsCollapsed && styles.inputsAsideCollapsed)}
            aria-label="Angaben"
          >
            {inputsCollapsed ? (
              <InputsRail
                expandRef={expandRef}
                onExpand={() => setCollapsed(false)}
                onGroup={(group) => {
                  setCollapsed(false);
                  setFocusGroup(group);
                }}
              />
            ) : (
              <div id="calculator-inputs" ref={inputsRef} className={styles.inputsInner}>
                <div className={styles.inputsHead}>
                  <span className={styles.eyebrow11}>Angaben</span>
                  <Tooltip content="Eingaben einklappen" placement="bottom-end">
                    <button
                      ref={collapseRef}
                      type="button"
                      className={clsx('pressable', styles.railButton)}
                      aria-label="Eingaben einklappen"
                      aria-expanded={true}
                      aria-controls="calculator-inputs"
                      onClick={() => setCollapsed(true)}
                    >
                      <Icon icon={PanelLeftClose} size={20} />
                    </button>
                  </Tooltip>
                </div>
                {inputs}
              </div>
            )}
          </aside>
        )}
        <div className={styles.viewColumn}>
          {view === 'berater' ? (
            <AdvisorTab
              context={context}
              values={values}
              result={customer.result}
              onCopySummary={() => {
                const text = buildTextSummary(values, customer.result, horizon, context.unit.address);
                navigator.clipboard.writeText(text).then(
                  () => toast({ message: 'Zusammenfassung kopiert.' }),
                  () => toast({ message: 'Die Zusammenfassung konnte nicht kopiert werden.' }),
                );
              }}
            />
          ) : (
            <ResultsTab
              context={context}
              values={values}
              view={customer}
              onYearChange={(year) => setValue('saleYear', year)}
              onInstallmentYearChange={(year) => setValue('installmentYear', year)}
              onSendPdf={() => setDialog('pdf')}
            />
          )}
        </div>
      </div>
      <SaveDialog
        open={dialog !== null}
        mode={dialog ?? 'save'}
        context={context}
        values={values}
        view={customer}
        onClose={() => setDialog(null)}
        onSaved={(calculation) => {
          setDialog(null);
          rememberSaved(calculation);
          toast({
            message: calculation.sentToCustomerAt
              ? `${calculation.numberLabel} gespeichert und als PDF gesendet.`
              : `${calculation.numberLabel} im Deal gespeichert.`,
          });
        }}
      />
    </div>
  );
}
